import { QuestionType, TableColumnType } from '../../core/constants/common';

export const isOptionsBasedAnswer = (type) =>
  type === QuestionType.MULTI_SELECT_DROP_DOWN
  || type === QuestionType.DROP_DOWN
  || type === QuestionType.RADIO
  || type === QuestionType.MULTIPLE_CHOICE
  || type === QuestionType.RADIO_GRID;

export const isActionNeeded = (type) =>
  type === QuestionType.RADIO
  || type === QuestionType.MULTI_SELECT_DROP_DOWN
  || type === QuestionType.DROP_DOWN
  || type === QuestionType.MULTIPLE_CHOICE
  || type === QuestionType.RADIO_GRID;

export const isRemarkNeeded = (type) => isActionNeeded(type);

export const constructGridConfigPayload = (gridData) => ({
  rows: gridData.rowSets.map((set) => ({
    id: set.id || null,
    name: set.name,
    helpText: set.help,
    sortOrder: set.sortOrder,
    active: true,
    code: set.code,
    cellOptions: set.rows.map((row, rowIndex) => ({
      id: row.id || null,
      name: row.option,
      score: row.score,
      sortOrder: row.sortOrder ? row.sortOrder : rowIndex + 1,
      valueCode: set.code,
      columnRef: `col-${rowIndex + 1}`,
    })),
  })),
  columns: gridData.columns.map((col, colIndex) => ({
    id: col.id || null,
    name: col.name,
    ref: `col-${colIndex + 1}`,
    sortOrder: col.sortOrder ? col.sortOrder : colIndex + 1,
    active: true,
  })),
});

export const mapGridConfigToEditorData = (gridConfig) => {
  if (!gridConfig) return { columns: [], rowSets: [] };

  const sourceColumns = gridConfig.columns || [];
  const columns = sourceColumns.map((col) => ({
    id: col.id,
    name: col.name,
    sortOrder: col.sortOrder,
  }));

  const rowSets = (gridConfig.rows || []).map((row) => ({
    id: row.id,
    name: row.name,
    code: row.code,
    help: row.helpText,
    sortOrder: row.sortOrder,
    rows: sourceColumns.map((col, colIndex) => {
      const columnRef = col.ref || `col-${colIndex + 1}`;
      const cell = row.cellOptions?.find((o) => o.columnId != null && col.id != null && o.columnId === col.id)
        || row.cellOptions?.find((o) => o.columnRef === columnRef)
        || row.cellOptions?.[colIndex];
      return {
        id: cell?.id || '',
        option: cell?.name || '',
        score: cell?.score ?? '',
        sortOrder: cell?.sortOrder || '',
      };
    }),
  }));

  return { columns, rowSets };
};

export const constructTableConfigPayload = (tableData) => ({
  id: tableData?.id || null,
  columns: (tableData?.columns || []).map((col, index) => ({
    id: col.id || null,
    name: col.name,
    sortOrder: col.sortOrder || index + 1,
    active: true,
    columnType: col.columnType || TableColumnType.TEXT,
    required: !!col.required,
    minimumValue: col.columnType === TableColumnType.NUMERIC ? (col.minimumValue ?? null) : null,
    maximumValue: col.columnType === TableColumnType.NUMERIC ? (col.maximumValue ?? null) : null,
  })),
});

export const mapTableConfigToEditorData = (tableConfig) => {
  if (!tableConfig) return { columns: [] };

  return {
    id: tableConfig.id,
    columns: (tableConfig.columns || []).map((col) => ({
      id: col.id,
      name: col.name,
      sortOrder: col.sortOrder,
      columnType: col.columnType || TableColumnType.TEXT,
      required: !!col.required,
      minimumValue: col.minimumValue ?? '',
      maximumValue: col.maximumValue ?? '',
    })),
  };
};

export const normalizeGridConfigForDuplicate = (gridConfig) => {
  if (!gridConfig) return gridConfig;

  const sourceColumns = gridConfig.columns || [];
  const columns = sourceColumns.map((col, colIndex) => ({
    name: col.name,
    ref: col.ref || `col-${colIndex + 1}`,
    sortOrder: col.sortOrder ?? colIndex + 1,
    active: col.active ?? true,
  }));

  const columnIdToRef = new Map();
  sourceColumns.forEach((col, colIndex) => {
    const ref = col.ref || `col-${colIndex + 1}`;
    if (col.id != null) columnIdToRef.set(col.id, ref);
    columnIdToRef.set(colIndex, ref);
  });

  const rows = (gridConfig.rows || []).map((row, rowIndex) => ({
    name: row.name,
    helpText: row.helpText,
    sortOrder: row.sortOrder ?? rowIndex + 1,
    active: row.active ?? true,
    code: row.code,
    cellOptions: (row.cellOptions || []).map((cell, cellIndex) => {
      let columnRef = cell.columnRef;
      if (!columnRef && cell.columnId != null && columnIdToRef.has(cell.columnId)) {
        columnRef = columnIdToRef.get(cell.columnId);
      }
      if (!columnRef) {
        columnRef = `col-${cellIndex + 1}`;
      }
      return {
        name: cell.name,
        score: cell.score,
        sortOrder: cell.sortOrder ?? cellIndex + 1,
        valueCode: cell.valueCode ?? row.code,
        columnRef,
      };
    }),
  }));

  return { columns, rows };
};

// layoutJson is an opaque, UI-owned blob (the backend only checks it's
// non-blank JSON), so its shape is whatever's easiest for the editor/preview
// to work with. Columns/rows are a flat ordered list — "add a column" is
// always just "append to this list" — and spanning headers are a separate,
// optional overlay that references existing column/row keys rather than a
// nesting structure, so grouping never gets in the way of the simple case.
export const constructGridQuestionConfigPayload = (gridQuestionData) => ({
  id: gridQuestionData?.id || null,
  layoutJson: JSON.stringify({
    columns: gridQuestionData?.columns || [],
    columnGroups: gridQuestionData?.columnGroups || [],
    rows: gridQuestionData?.rows || [],
    rowGroups: gridQuestionData?.rowGroups || [],
  }),
  cellLinks: Object.entries(gridQuestionData?.cellLinks || {})
    .filter(([, link]) => link?.linkedFieldId)
    .map(([cellKey, link]) => ({
      id: link.id || null,
      cellKey,
      linkedFieldId: link.linkedFieldId,
      hideLinkedField: link.hideLinkedField !== false,
    })),
});

export const mapGridQuestionConfigToEditorData = (gridQuestionConfig) => {
  if (!gridQuestionConfig) return { id: null, columns: [], columnGroups: [], rows: [], rowGroups: [], cellLinks: {} };

  let layout = {};
  try {
    layout = JSON.parse(gridQuestionConfig.layoutJson || '{}') || {};
  } catch {
    layout = {};
  }

  const cellLinks = (gridQuestionConfig.cellLinks || []).reduce((acc, link) => {
    acc[link.cellKey] = {
      id: link.id,
      linkedFieldId: link.linkedFieldId,
      linkedFieldType: link.linkedFieldType,
      hideLinkedField: link.hideLinkedField !== false,
    };
    return acc;
  }, {});

  return {
    id: gridQuestionConfig.id,
    columns: layout.columns || [],
    columnGroups: layout.columnGroups || [],
    rows: layout.rows || [],
    rowGroups: layout.rowGroups || [],
    cellLinks,
  };
};

export const normalizeGridQuestionConfigForDuplicate = (gridQuestionConfig) => {
  if (!gridQuestionConfig) return gridQuestionConfig;
  // Cell links are an exclusive 1:1 binding to specific fields elsewhere on the
  // form — those fields aren't duplicated along with this one, so links can't
  // be carried over. Keep only the layout (headers/structure) for relinking.
  return { layoutJson: gridQuestionConfig.layoutJson, cellLinks: [] };
};

export const normalizeTableConfigForDuplicate = (tableConfig) => {
  if (!tableConfig) return tableConfig;

  return {
    columns: (tableConfig.columns || []).map((col, colIndex) => ({
      name: col.name,
      sortOrder: col.sortOrder ?? colIndex + 1,
      active: col.active ?? true,
      columnType: col.columnType,
      required: col.required ?? false,
      minimumValue: col.minimumValue ?? null,
      maximumValue: col.maximumValue ?? null,
    })),
  };
};

export const buildOptionIdMap = (sourceField, newField) => {
  const map = new Map();
  if (!sourceField || !newField) return map;

  (sourceField.options || []).forEach((srcOpt, index) => {
    const newOpt = (newField.options || [])[index]
      || (newField.options || []).find((o) => o.name === srcOpt.name && o.sortOrder === srcOpt.sortOrder);
    if (srcOpt.id != null && newOpt?.id != null) {
      map.set(srcOpt.id, newOpt.id);
    }
  });

  (sourceField.gridConfig?.rows || []).forEach((srcRow, rowIndex) => {
    const newRow = newField.gridConfig?.rows?.[rowIndex];
    (srcRow.cellOptions || []).forEach((srcCell, cellIndex) => {
      const newCell = newRow?.cellOptions?.[cellIndex];
      if (srcCell.id != null && newCell?.id != null) {
        map.set(srcCell.id, newCell.id);
      }
    });
  });

  return map;
};

export const remapOptionId = (optionId, optionIdMap) => {
  if (optionId == null || optionId === 0) return optionId ?? 0;
  return optionIdMap.get(optionId) ?? optionId;
};

export const cloneActionsForDuplicate = (actions, optionIdMap) => (
  (actions || []).map((action) => {
    const copy = JSON.parse(JSON.stringify(action));
    delete copy.id;
    if (copy.formOption?.id != null) {
      copy.formOption.id = remapOptionId(copy.formOption.id, optionIdMap);
    }
    return copy;
  })
);

export const cloneLogicsForDuplicate = (logics, optionIdMap) => (
  (logics || []).map((logic) => ({
    action: logic.action,
    rules: (logic.rules || []).map((rule) => ({
      operation: rule.operation,
      value: rule.value,
      option: {
        id: remapOptionId(rule.option?.id, optionIdMap) ?? 0,
      },
    })),
    displayFields: (logic.displayFields || []).map((displayField) => ({
      sectionId: displayField.sectionId ?? 0,
      subSectionId: displayField.subSectionId ?? 0,
      fields: (displayField.fields || []).map((field) => ({ id: field.id })),
    })),
  }))
);

export const cloneFieldPayloadForDuplicate = (field, submitLanguage) => {
  const copy = JSON.parse(JSON.stringify(field));
  delete copy.id;
  delete copy.actions;
  delete copy.logicDefinitions;
  delete copy.logics;
  copy.name = `${copy.name || 'Untitled question'} (copy)`;
  copy.selectedLanguage = { id: submitLanguage };
  copy.sortOrder = copy.sortOrder ?? 100;
  copy.active = copy.active ?? true;

  copy.options?.forEach((option) => {
    delete option.id;
  });

  if (copy.gridConfig) {
    copy.gridConfig = normalizeGridConfigForDuplicate(copy.gridConfig);
  }
  if (copy.tableConfig) {
    copy.tableConfig = normalizeTableConfigForDuplicate(copy.tableConfig);
  }
  if (copy.gridQuestionConfig) {
    copy.gridQuestionConfig = normalizeGridQuestionConfigForDuplicate(copy.gridQuestionConfig);
  }

  return copy;
};

export const buildFieldPayload = ({
  field,
  selectedAnswerType,
  optional,
  scoreEnabled,
  helpText,
  submitLanguage,
  choiceOptions,
  remarksArr,
  remarkTitle,
  remarkRequired,
  allowComments,
  otherChecked,
  otherText,
  otherMandatory,
  uomRequired,
  uomData,
  extDependency,
  extConfig,
  allowFileUpload,
  fileMandatory,
  hisDataSupported,
  gridData,
  tableData,
  gridQuestionData,
  optionSelectionValidation,
  fieldEditId,
}) => {
  const baseLanguage = { selectedLanguage: { id: submitLanguage } };

  if (isOptionsBasedAnswer(selectedAnswerType)) {
    const options = choiceOptions.map((x) => {
      const flag = remarksArr?.find((y) => y.id === x.id);
      const withRemarks = flag
        ? { ...x, showRemarks: true, remarksMandatory: !!remarkRequired }
        : { ...x, showRemarks: false, remarksMandatory: false };
      return withRemarks.showOthers ? { ...withRemarks, name: otherText } : withRemarks;
    });

    return {
      name: field,
      sortOrder: 100,
      fieldHelpText: helpText,
      type: selectedAnswerType,
      optional,
      active: true,
      randomize: false,
      allowOtherOption: otherChecked,
      otherTextLabel: otherText,
      otherFieldValidation: { mandatory: otherMandatory, validationMessage: null },
      allowRemark: remarksArr.length > 0 || allowComments,
      remarkMandatory: remarkRequired,
      remarkLabel: remarksArr.length > 0 ? remarkTitle : '',
      remarkFieldValidation: { mandatory: false, validationMessage: null },
      scoreEnabled,
      responseAcknowledgement: null,
      ...baseLanguage,
      options,
      uomRequired,
      extDependency: extDependency.enabled ? extDependency.value : null,
      extConfig: extConfig ?? null,
      allowFileUpload,
      fileMandatory,
      historicalDataSupported: hisDataSupported,
      gridConfig: selectedAnswerType === QuestionType.RADIO_GRID
        ? constructGridConfigPayload(gridData)
        : undefined,
      optionSelectionValidation: (optionSelectionValidation.operator && optionSelectionValidation.count)
        ? {
          operator: optionSelectionValidation.operator,
          count: optionSelectionValidation.count,
          maximumCount: optionSelectionValidation.maximumCount || null,
          validationMessage: optionSelectionValidation.validationMessage || null,
        }
        : null,
      ...(fieldEditId ? { id: fieldEditId } : {}),
    };
  }

  return {
    name: field,
    optional,
    active: true,
    type: selectedAnswerType,
    displayTitle: true,
    sortOrder: 100,
    fieldHelpText: helpText,
    ...baseLanguage,
    uomRequired,
    relevantUoms: uomData.relevantUoms,
    defaultUOM: uomData.defaultUOM,
    extDependency: extDependency.enabled ? extDependency.value : null,
    extConfig: extConfig ?? null,
    allowFileUpload,
    fileMandatory,
    allowRemark: remarksArr.length > 0 || allowComments,
    remarkMandatory: remarkRequired,
    historicalDataSupported: hisDataSupported,
    tableConfig: selectedAnswerType === QuestionType.TABLE
      ? constructTableConfigPayload(tableData)
      : undefined,
    gridQuestionConfig: selectedAnswerType === QuestionType.GRID
      ? constructGridQuestionConfigPayload(gridQuestionData)
      : undefined,
    ...(fieldEditId ? { id: fieldEditId } : {}),
  };
};
